import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import "./PageLoader.css";

export default function PageLoader() {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [zoomOut, setZoomOut] = useState(false);

  useEffect(() => {
    // Bloquer le scroll pendant le chargement
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";

    // Simuler le chargement minimum de 2.5s pour l'animation complète
    const minLoadTime = 2500;
    const startTime = Date.now();

    const handleLoad = () => {
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, minLoadTime - elapsedTime);

      setTimeout(() => {
        // Réactiver le scroll dès que le chargement est terminé
        document.documentElement.style.overflow = "unset";
        document.body.style.overflow = "unset";

        setZoomOut(true);
        // Retirer complètement le loader après l'animation de zoom (2s)
        setTimeout(() => {
          setIsLoading(false);
        }, 2000); // Durée de l'animation de zoom
      }, remainingTime);
    };

    // Si la page est déjà chargée
    if (document.readyState === "complete") {
      handleLoad();
    } else {
      window.addEventListener("load", handleLoad);
      return () => window.removeEventListener("load", handleLoad);
    }

    // Cleanup au cas où le composant se démonte
    return () => {
      document.documentElement.style.overflow = "unset";
      document.body.style.overflow = "unset";
    };
  }, []);

  if (!isLoading) return null;

  return (
    <div className={`page-loader ${zoomOut ? "zoom-out" : ""}`}>
      <div className="loader-content">
        <div className={`loader-logo-animated ${zoomOut ? "zoom-effect" : ""}`}>
          <svg
            className={`logo-svg ${zoomOut ? "svg-zoom" : ""}`}
            width="150"
            height="55"
            viewBox="0 0 75 55"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Carré 1 - Position gauche */}
            <g className="square square-1">
              <path
                d="M1.81962e-07 25.753V13.726C-7.39616e-05 13.5416 0.0486405 13.3605 0.141198 13.201C0.233756 13.0415 0.366861 12.9094 0.527 12.818L22.717 0.137992C22.8759 0.0470585 23.0559 -0.000525738 23.239 4.3814e-06C23.4221 0.000534501 23.6019 0.0491604 23.7602 0.141012C23.9186 0.232864 24.0501 0.364718 24.1415 0.523369C24.2329 0.68202 24.281 0.861899 24.281 1.04499V12.808C24.2805 12.9902 24.2323 13.1692 24.1413 13.3271C24.0502 13.4849 23.9195 13.6163 23.762 13.708L1.572 26.656C1.41317 26.7487 1.23268 26.7979 1.04875 26.7986C0.864821 26.7993 0.683977 26.7514 0.524488 26.6597C0.364999 26.5681 0.232515 26.436 0.140423 26.2768C0.0483306 26.1176 -0.000108344 25.9369 1.81962e-07 25.753Z"
                fill="white"
              />
            </g>

            {/* Carré 2 - Position milieu */}
            <g className="square square-2">
              <path
                d="M1.81962e-07 25.753V13.726C-7.39616e-05 13.5416 0.0486405 13.3605 0.141198 13.201C0.233756 13.0415 0.366861 12.9094 0.527 12.818L22.717 0.137992C22.8759 0.0470585 23.0559 -0.000525738 23.239 4.3814e-06C23.4221 0.000534501 23.6019 0.0491604 23.7602 0.141012C23.9186 0.232864 24.0501 0.364718 24.1415 0.523369C24.2329 0.68202 24.281 0.861899 24.281 1.04499V12.808C24.2805 12.9902 24.2323 13.1692 24.1413 13.3271C24.0502 13.4849 23.9195 13.6163 23.762 13.708L1.572 26.656C1.41317 26.7487 1.23268 26.7979 1.04875 26.7986C0.864821 26.7993 0.683977 26.7514 0.524488 26.6597C0.364999 26.5681 0.232515 26.436 0.140423 26.2768C0.0483306 26.1176 -0.000108344 25.9369 1.81962e-07 25.753Z"
                fill="white"
              />
            </g>

            {/* Carré 3 - Position droite (celui qui va sauter) */}
            <g className="square square-3">
              <path
                d="M1.81962e-07 25.753V13.726C-7.39616e-05 13.5416 0.0486405 13.3605 0.141198 13.201C0.233756 13.0415 0.366861 12.9094 0.527 12.818L22.717 0.137992C22.8759 0.0470585 23.0559 -0.000525738 23.239 4.3814e-06C23.4221 0.000534501 23.6019 0.0491604 23.7602 0.141012C23.9186 0.232864 24.0501 0.364718 24.1415 0.523369C24.2329 0.68202 24.281 0.861899 24.281 1.04499V12.808C24.2805 12.9902 24.2323 13.1692 24.1413 13.3271C24.0502 13.4849 23.9195 13.6163 23.762 13.708L1.572 26.656C1.41317 26.7487 1.23268 26.7979 1.04875 26.7986C0.864821 26.7993 0.683977 26.7514 0.524488 26.6597C0.364999 26.5681 0.232515 26.436 0.140423 26.2768C0.0483306 26.1176 -0.000108344 25.9369 1.81962e-07 25.753Z"
                fill="white"
              />
            </g>
          </svg>

          <div className={`logo-text ${zoomOut ? "fade-away" : ""}`}>
            MAELCORP
          </div>
        </div>

        <p className={`loader-text ${zoomOut ? "fade-away" : ""}`}>
          {t("pageLoader.loading")}
        </p>
      </div>
    </div>
  );
}
